/**
 * The interpreter state machine — pure, platform-free, and the single place
 * the web client decides what the HUD shows. RN has an independent v2
 * implementation today; importing this module there remains a follow-up.
 *
 * No React, no Socket.IO, no storage, no timers. Given a state and an event it
 * returns the next state, which means the whole rendering contract can be
 * tested from JSON fixtures — the same fixtures the backend metrics run over —
 * without a device, a simulator or a browser.
 */
import {
  INITIAL_INTERPRETER_STATE,
  LIVE_SESSION_STATES,
  type InterpreterDirection,
  type InterpreterEvent,
  type InterpreterState,
  type InterpreterUtterance,
} from './types';

/**
 * A `final` utterance carrying no text at all is the worker's terminal-clear:
 * it emits one when the echo guard, the stray-direction guard or same-language
 * silence suppresses a turn, purely so the client drops the partial the user is
 * currently watching. It is a DELETE instruction, not content — rendering it
 * would leave an empty bubble on screen forever.
 */
export function isTerminalClear(u: InterpreterUtterance): boolean {
  return u.final && !u.sourceText.trim() && !u.targetText.trim();
}

/**
 * Later revisions of the same utterance win, but a stale/out-of-order delivery
 * must not clobber a newer one. Socket.IO preserves order per connection; a
 * reconnect replaying the gateway's buffer does not.
 */
function isNewer(incoming: InterpreterUtterance, existing?: InterpreterUtterance): boolean {
  if (!existing) return true;
  if (incoming.revision !== existing.revision) return incoming.revision > existing.revision;
  // Equal revision: a final always supersedes a partial.
  return incoming.final && !existing.final;
}

export function interpreterReducer(
  state: InterpreterState,
  event: InterpreterEvent,
): InterpreterState {
  switch (event.type) {
    case 'session': {
      const s = event.payload;
      const active = LIVE_SESSION_STATES.includes(s.state);
      // `ended`/`error` clear the board. Leaving stale utterances on screen
      // after the worker is gone is how a dead session kept looking live.
      if (!active) {
        return { ...state, active: false, session: s, utterances: {}, suggestion: null };
      }
      return { ...state, active: true, session: s };
    }

    case 'utterance': {
      const u = event.payload;
      const existing = state.utterances[u.id];
      if (!isNewer(u, existing)) return state;

      if (isTerminalClear(u)) {
        if (!existing) return state;
        const next = { ...state.utterances };
        delete next[u.id];
        return { ...state, utterances: next };
      }
      return { ...state, utterances: { ...state.utterances, [u.id]: u } };
    }

    case 'error': {
      const e = event.payload;
      // An utterance-scoped drop also removes the thing it refers to, so the
      // user is not left staring at a partial that will never complete.
      if (e.kind === 'utterance_dropped' && e.utteranceId && state.utterances[e.utteranceId]) {
        const next = { ...state.utterances };
        delete next[e.utteranceId];
        return { ...state, utterances: next, lastError: e };
      }
      return { ...state, lastError: e };
    }

    case 'suggestion':
      // Stored for display only. Applying a language switch is the user's call;
      // auto-applying on a confidence score is how the 2026-04 drift incident
      // silently changed languages mid-conversation.
      return { ...state, suggestion: event.payload };

    case 'dismissSuggestion':
      return { ...state, suggestion: null };

    case 'setViewMode':
      return { ...state, viewMode: event.payload };

    case 'reset':
      return { ...INITIAL_INTERPRETER_STATE };

    default:
      return state;
  }
}

/**
 * Render order. Sorts by `seq` then `tStart`, both of which are now stable per
 * utterance — see the note on InterpreterUtterance.seq for why sorting by a
 * re-stamped seq made the transcript reorder itself while being read.
 */
export function orderedUtterances(state: InterpreterState): InterpreterUtterance[] {
  return Object.values(state.utterances).sort(
    (a, b) => a.seq - b.seq || a.tStart - b.tStart,
  );
}

/**
 * What one panel shows for one utterance.
 *
 * Both panels render EVERY utterance — that is correct for a table-top
 * interpreter: each side sees its own words large and the other side's
 * translation large. `panelSide` is which half is asking.
 */
export function panelTexts(
  u: InterpreterUtterance,
  panelSide: 'traveler' | 'local',
): { primary: string; echo: string } {
  const isOwnSide =
    panelSide === 'traveler'
      ? u.direction === 'traveler_to_local'
      : u.direction === 'local_to_traveler';

  return isOwnSide
    ? { primary: u.sourceText, echo: '' }
    : { primary: u.targetText || u.sourceText, echo: u.sourceText };
}

/** True while an utterance is still moving — drives the status glyph. */
export function isSettled(u: InterpreterUtterance): boolean {
  return u.final || u.status === 'done' || u.status === 'dropped';
}

export function directionForSpeaker(side: 'traveler' | 'local'): InterpreterDirection {
  return side === 'traveler' ? 'traveler_to_local' : 'local_to_traveler';
}
